/**
 * Context: ThemeContext
 * SpecReferenceID: FRONTEND-WEB-V1
 * Requirements: FRONTEND-WEB-V1-R10, FRONTEND-WEB-V1-R14
 *
 * Provides theme state and toggle functionality throughout the application.
 * FRONTEND-WEB-V1-R10: Toggle between light and dark mode.
 * FRONTEND-WEB-V1-R14: WCAG 2.1 AA compliant theme implementation.
 *
 * Risk Links: R10
 * FMEA Links: FM10
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';

/**
 * Available theme options.
 * FRONTEND-WEB-V1-R10: Support light, dark, and system preference modes.
 */
export type Theme = 'light' | 'dark' | 'system';

/**
 * Resolved theme (actual applied theme, not 'system').
 */
export type ResolvedTheme = 'light' | 'dark';

/**
 * Theme context state shape.
 */
interface ThemeContextState {
  /** Current theme setting (light, dark, or system) */
  theme: Theme;
  /** Resolved theme (light or dark) - what's actually applied */
  resolvedTheme: ResolvedTheme;
  /** Set the theme */
  setTheme: (theme: Theme) => void;
  /** Toggle between light and dark (ignores system) */
  toggleTheme: () => void;
}

/**
 * Local storage key for persisting theme preference.
 */
const THEME_STORAGE_KEY = 'galen-portal-theme';

/**
 * Get the system color scheme preference.
 * FRONTEND-WEB-V1-R10: Respect user's system preference.
 */
function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') {
    return 'light';
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * Resolve theme setting to actual applied theme.
 */
function resolveTheme(theme: Theme): ResolvedTheme {
  if (theme === 'system') {
    return getSystemTheme();
  }
  return theme;
}

/**
 * Get stored theme from localStorage.
 */
function getStoredTheme(): Theme {
  if (typeof window === 'undefined') {
    return 'system';
  }
  const stored = localStorage.getItem(THEME_STORAGE_KEY);
  if (stored === 'light' || stored === 'dark' || stored === 'system') {
    return stored;
  }
  return 'system';
}

/**
 * Apply theme to document root element.
 * FRONTEND-WEB-V1-R10: Toggle between light and dark mode.
 */
function applyTheme(resolvedTheme: ResolvedTheme): void {
  const root = document.documentElement;

  // Remove existing theme classes
  root.classList.remove('light', 'dark');

  // Add new theme class
  root.classList.add(resolvedTheme);

  // Update color-scheme CSS property for native elements
  root.style.colorScheme = resolvedTheme;
}

/**
 * Theme context - provides theme state throughout the app.
 */
const ThemeContext = createContext<ThemeContextState | undefined>(undefined);

/**
 * Props for ThemeProvider component.
 */
interface ThemeProviderProps {
  children: ReactNode;
  /** Default theme (defaults to 'system') */
  defaultTheme?: Theme;
}

/**
 * ThemeProvider component.
 *
 * FRONTEND-WEB-V1-R10: Provides theme toggle functionality.
 *
 * Usage:
 * ```tsx
 * <ThemeProvider>
 *   <App />
 * </ThemeProvider>
 * ```
 */
export function ThemeProvider({
  children,
  defaultTheme = 'system',
}: ThemeProviderProps): React.ReactElement {
  const [theme, setThemeState] = useState<Theme>(() => {
    // SSR safety: start with default, hydrate from storage on client
    if (typeof window === 'undefined') {
      return defaultTheme;
    }
    return getStoredTheme();
  });

  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() =>
    resolveTheme(theme)
  );

  /**
   * Set theme and persist to localStorage.
   */
  const setTheme = useCallback((newTheme: Theme): void => {
    setThemeState(newTheme);
    localStorage.setItem(THEME_STORAGE_KEY, newTheme);
  }, []);

  /**
   * Toggle between light and dark mode.
   * FRONTEND-WEB-V1-R10: Simple toggle functionality.
   */
  const toggleTheme = useCallback((): void => {
    setTheme(resolvedTheme === 'light' ? 'dark' : 'light');
  }, [resolvedTheme, setTheme]);

  /**
   * Apply theme when it changes.
   */
  useEffect(() => {
    const resolved = resolveTheme(theme);
    setResolvedTheme(resolved);
    applyTheme(resolved);
  }, [theme]);

  /**
   * Listen for system theme changes when using 'system' setting.
   * FRONTEND-WEB-V1-R10: Respond to system preference changes.
   */
  useEffect(() => {
    if (theme !== 'system') {
      return;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent): void => {
      const newResolved = e.matches ? 'dark' : 'light';
      setResolvedTheme(newResolved);
      applyTheme(newResolved);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  /**
   * Memoize context value to prevent unnecessary re-renders.
   */
  const contextValue = useMemo<ThemeContextState>(
    () => ({
      theme,
      resolvedTheme,
      setTheme,
      toggleTheme,
    }),
    [theme, resolvedTheme, setTheme, toggleTheme]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * Hook to access theme context.
 *
 * FRONTEND-WEB-V1-R10: Access theme state and toggle functionality.
 *
 * Usage:
 * ```tsx
 * const { theme, setTheme, toggleTheme } = useTheme();
 * ```
 */
export function useTheme(): ThemeContextState {
  const context = useContext(ThemeContext);

  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }

  return context;
}
